import type { ComponentType, ReactNode, ReactNodeArray } from 'react';

import ToastContextProvider from '../../contexts/ToastContext';
import { window } from '../../globals';
import Redirect from '../generic/Redirect';
import Toaster from '../Toaster/Toaster';
import type { RouterProps } from '../../types';

type AuthProps = {
  children: ReactNode | ReactNodeArray;
  location: RouterProps['location'];
};

export type AuthPage = 'login' | 'register';

/**
 * Root-level for authentication views.
 *
 * Carries its own toast provider and `Toaster`. `/auth` is a sibling of the `Application` route rather than a
 * child of it, so it never inherits the one mounted there — and without a provider `useAddToast` resolves to
 * `undefined` off the context default, so the first toast threw `addToast is not a function` and took the
 * page down. The login and register flows toast for resends, resolve failures and login errors, so they need
 * a real one. The two never render at once, which is what `Toaster`'s "only once in the application" note is
 * guarding against.
 */
const Auth = ({ children, location }: AuthProps) => (
  <ToastContextProvider>
    <main className="fossa-auth">
      {children}
    </main>
    <Toaster location={location} />
  </ToastContextProvider>
);

//  Higher-order component which renders a redirect if the user is already logged in.
const withRedirectOnLoggedIn = (AuthComponent: ComponentType<AuthProps>) => function (props: AuthProps = {} as AuthProps) {
  //  If there is a user but they don't have an org, we still render the auth component (the user needs to create/select an org).
  const isLoggedIn: boolean = !!window.user?.organization;
  return isLoggedIn
    ? <Redirect to="/" replace />
    : <AuthComponent {...props} />;
};

export default withRedirectOnLoggedIn(Auth);
